// ============================================================
// ReviewManager.jsx
// Manages both manual and automatic review prompts
// Uses Shopify App Bridge Reviews API for in-app modal
// ============================================================

import { useEffect, useCallback, useRef } from "react";
import { useAppBridge } from "@shopify/app-bridge-react";
import axios from "axios";

/**
 * ReviewManager Component
 * 
 * This component handles:
 * 1. Automatic review prompts (after 10 days, respecting rate limits)
 * 2. Provides a trigger function for manual prompts (button clicks)
 * 
 * IMPORTANT: Both automatic and manual triggers use in-app modal.
 * We do NOT record prompts to backend to keep the button visible.
 * 
 * Usage:
 * <ReviewManager 
 *   shopOrigin={shopOrigin} 
 *   onTriggerAvailable={(triggerFn) => setReviewTrigger(() => triggerFn)}
 * />
 */
export default function ReviewManager({ shopOrigin, onTriggerAvailable }) {
  const shopify = useAppBridge();
  const hasAutoTriggered = useRef(false);
  const eligibilityChecked = useRef(false);

  // ============================================================
  // TRIGGER REVIEW MODAL
  // Called either automatically or manually (button click)
  // Always uses Shopify in-app modal
  // ============================================================
  const triggerReviewModal = useCallback(async (type = 'manual') => {
    try {
      console.log(`Triggering review modal (${type})`);

      // For manual triggers, check eligibility first
      if (type === 'manual') {
        try {
          const eligibility = await axios.get(
            `/api/get-review-eligibility/?shop=${shopOrigin}&type=${type}`
          );

          // If not eligible due to rate limiting, but installed > 1 year, redirect to App Store
          if (!eligibility.data.eligible && eligibility.data.should_redirect) {
            window.open(eligibility.data.redirect_url, '_blank');
            console.log('Manual review redirect (yearly limit reached)');
            return { success: true, redirected: true };
          }
        } catch (error) {
          console.error('Error checking manual eligibility:', error);
          // Continue to show modal even if eligibility check fails
        }
      }

      // Trigger Shopify's native in-app review modal
      try {
        console.log('Shopify Reviews API available:', !!shopify.reviews);
        shopify.reviews.request();
        console.log(`In-app review modal triggered successfully (${type})`);

        // ============================================================
        // CRITICAL: DO NOT record prompt to backend
        // We can't detect if user actually submitted the review
        // This keeps the Share Feedback button visible
        // ============================================================

        return { success: true, modalShown: true };
        
      } catch (error) {
        console.error('Shopify reviews API error:', error);

        // Fallback: open review URL directly if in-app modal fails
        window.open(
          'https://apps.shopify.com/bloghero#modal-show=WriteReviewModal',
          '_blank'
        );
        console.log(`Review fallback to App Store URL (${type})`);
        return { success: true, fallback: true };
      }

    } catch (error) {
      console.error('Error triggering review modal:', error);
      return { success: false, error: error.message };
    }
  }, [shopOrigin, shopify]);

  // ============================================================
  // AUTOMATIC REVIEW PROMPT
  // Checks eligibility and shows modal after 10 days
  // Only runs once per session
  // ============================================================
  useEffect(() => {
    if (!shopOrigin || hasAutoTriggered.current || eligibilityChecked.current) {
      return;
    }

    const checkAndTriggerAutoReview = async () => {
      try {
        eligibilityChecked.current = true;

        // Check if user has permanently dismissed
        const dismissed = localStorage.getItem('bloghero_review_auto_dismissed');
        if (dismissed === 'true') {
          console.log('Auto review permanently dismissed by user');
          return;
        }

        // Check if already shown in this session
        const alreadyShownThisSession = sessionStorage.getItem('bloghero_auto_review_shown');
        if (alreadyShownThisSession === 'true') {
          console.log('Auto review already shown this session');
          return;
        }

        // Check eligibility
        const response = await axios.get(
          `/api/get-review-eligibility/?shop=${shopOrigin}&type=automatic`
        );

        const { eligible, should_auto_prompt, reason } = response.data;

        if (!eligible) {
          console.log('Not eligible for auto review:', reason);
          return;
        }

        if (should_auto_prompt && !hasAutoTriggered.current) {
          // Wait a bit (5 seconds) after dashboard loads before showing
          setTimeout(async () => {
            hasAutoTriggered.current = true;
            const triggered = await triggerReviewModal('automatic');

            if (triggered && triggered.success) {
              console.log('Automatic review prompt shown');
              
              // Store in sessionStorage that we showed it this session
              // This prevents showing again if user refreshes
              sessionStorage.setItem('bloghero_auto_review_shown', 'true');
            }
          }, 2000);
        }

      } catch (error) {
        console.error('Error checking auto review eligibility:', error);
      }
    };

    checkAndTriggerAutoReview();
  }, [shopOrigin, triggerReviewModal]);

  // ============================================================
  // PROVIDE MANUAL TRIGGER TO PARENT
  // Allows Dashboard to call review modal on button click
  // ============================================================
  useEffect(() => {
    if (onTriggerAvailable) {
      onTriggerAvailable(triggerReviewModal);
    }
  }, [triggerReviewModal, onTriggerAvailable]);

  // This component doesn't render anything
  return null;
}

// ============================================================
// HELPER HOOK: useReviewTrigger
// Simpler way to use ReviewManager
// For manual triggers - shows in-app modal
// ============================================================
export function useReviewTrigger(shopOrigin) {
  const shopify = useAppBridge();

  const triggerReview = useCallback(async () => {
    try {
      console.log('Manual review trigger via hook');

      // Try to show Shopify's in-app review modal
      try {
        console.log('Shopify Reviews API available:', !!shopify.reviews);
        shopify.reviews.request();
        console.log('In-app review modal triggered successfully');
        return { success: true, modalShown: true };
      } catch (error) {
        console.error('Shopify reviews API error:', error);

        // Fallback: open App Store URL if in-app modal fails
        window.open(
          'https://apps.shopify.com/bloghero#modal-show=WriteReviewModal',
          '_blank'
        );
        console.log('Review fallback to App Store URL');
        return { success: true, fallback: true };
      }

    } catch (error) {
      console.error('Review trigger error:', error);
      return { success: false, error: error.message };
    }
  }, [shopOrigin, shopify]);

  return triggerReview;
}